/**
 * aiExplainerCache.js — localStorage-backed cache for AI Explainer
 * responses. Keyed by a stable hash of (workflowId, nodeId, nodeType,
 * workflowContext) so the cache hits whenever the user revisits a node
 * AND nothing about the explanation context has changed.
 *
 * Why localStorage rather than in-memory only:
 *   - Survives page reloads (huge UX win — no waiting on cold start)
 *   - Survives panel collapse / tab switch / route changes
 *   - Survives navigation between /canvas-v3 and other routes
 *   - Per-tenant scoped (one localStorage per browser profile)
 *
 * Eviction strategy:
 *   - LRU with a hard cap on entry count (default 200)
 *   - Optional TTL (default 30 days) — long enough that real-world
 *     workflows don't churn it; short enough to retire stale model
 *     outputs after a major prompt-template change
 *   - Atomic writes — we read, mutate, and write the whole object
 *     under one localStorage key so partial corruption is impossible
 *
 * The cache is intentionally namespaced under `agentlets.ai.explainer.v1`
 * — the v1 suffix means we can bump the prompt template and force a
 * cache wipe by bumping to v2, without leaking stale entries to users
 * who happen to keep their old localStorage.
 */

const STORAGE_KEY = 'agentlets.ai.explainer.v1';
const DEFAULT_MAX_ENTRIES = 200;
const DEFAULT_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

// Cheap djb2-style hash for stable cache keys. We don't need crypto
// strength — just need (string → ~32-bit int) so different inputs
// produce different cache slots and same inputs always collide.
function hash(str) {
  let h = 5381;
  const s = String(str);
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

// Stable JSON stringify with sorted keys so {a:1,b:2} and {b:2,a:1}
// hash to the same cache slot. Recurses through objects only one level
// deep — the workflowContext object we pass is always flat strings.
function stableStringify(obj) {
  if (obj == null) return 'null';
  if (typeof obj !== 'object') return JSON.stringify(obj);
  if (Array.isArray(obj)) return '[' + obj.map(stableStringify).join(',') + ']';
  const keys = Object.keys(obj).sort();
  const pairs = keys.map((k) => JSON.stringify(k) + ':' + stableStringify(obj[k]));
  return '{' + pairs.join(',') + '}';
}

/**
 * Build a cache key from explanation inputs. Same inputs → same key.
 *
 * @param {Object} parts
 * @param {string} parts.workflowId    — the loaded workflow's id (or '__local__')
 * @param {string} parts.nodeId        — the canvas-shape node id
 * @param {string} parts.nodeType      — type string (http_task / condition / …)
 * @param {Object} [parts.workflowContext] — the user-supplied context block
 * @returns {string} a hash key safe to use in localStorage
 */
export function makeKey({ workflowId, nodeId, nodeType, workflowContext }) {
  const blob = stableStringify({
    w: workflowId || '__local__',
    n: nodeId || '',
    t: nodeType || '',
    ctx: workflowContext || null,
  });
  return hash(blob);
}

function readStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { entries: {}, order: [] };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { entries: {}, order: [] };
    return {
      entries: parsed.entries || {},
      order: Array.isArray(parsed.order) ? parsed.order : [],
    };
  } catch (_) {
    return { entries: {}, order: [] };
  }
}

function writeStore(store) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (_) {
    // Quota or private mode — silently no-op. We're a cache; missing
    // it means we just refetch from the API, which is acceptable.
  }
}

/**
 * Read a cached entry. Returns null if missing OR expired.
 */
export function readEntry(key, ttlMs = DEFAULT_TTL_MS) {
  if (!key) return null;
  const store = readStore();
  const entry = store.entries[key];
  if (!entry) return null;
  if (entry.ts && (Date.now() - entry.ts) > ttlMs) {
    // Expired — drop it on the next write.
    return null;
  }
  // LRU bump: move this key to the end of `order` so it ages last.
  const newOrder = store.order.filter((k) => k !== key);
  newOrder.push(key);
  if (newOrder.length !== store.order.length || newOrder[newOrder.length - 1] !== key) {
    writeStore({ ...store, order: newOrder });
  }
  return entry.value || null;
}

/**
 * Write an entry. Evicts oldest entries when over `maxEntries`.
 */
export function writeEntry(key, value, opts = {}) {
  if (!key || value == null) return;
  const maxEntries = opts.maxEntries || DEFAULT_MAX_ENTRIES;
  const store = readStore();
  const next = {
    entries: { ...store.entries, [key]: { value, ts: Date.now() } },
    order: store.order.filter((k) => k !== key).concat(key),
  };
  while (next.order.length > maxEntries) {
    const evict = next.order.shift();
    delete next.entries[evict];
  }
  writeStore(next);
}

/**
 * Drop a single key from the cache (e.g., on a manual "regenerate" click).
 */
export function evictEntry(key) {
  if (!key) return;
  const store = readStore();
  if (!store.entries[key]) return;
  const nextEntries = { ...store.entries };
  delete nextEntries[key];
  writeStore({
    entries: nextEntries,
    order: store.order.filter((k) => k !== key),
  });
}

/**
 * Wipe the whole cache. Useful for a Settings → "Reset AI cache" admin
 * action, or after a major prompt-template change in dev.
 */
export function clearAll() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    // noop
  }
}

/**
 * Cheap stats for the /flags page or a debug surface.
 */
export function stats() {
  const store = readStore();
  return {
    count: store.order.length,
    bytes: (() => {
      try {
        return (localStorage.getItem(STORAGE_KEY) || '').length;
      } catch (_) { return 0; }
    })(),
  };
}
